import type { Lang } from '../types';

export interface ReservationsText {
  pageTitle: string;
  addReservation: string;
  filterTitle: string;
  filterApartment: string;
  filterAllApartments: string;
  filterCheckInAfter: string;
  filterCheckOutBefore: string;
  filterGuestName: string;
  filterGuestPhone: string;
  searchByName: string;
  searchInNotes: string;
  loadingReservations: string;
  noReservations: string;
  newReservationTitle: string;
  formApartment: string;
  formGuestName: string;
  formCheckInDate: string;
  formCheckOutDate: string;
  formNumberOfGuests: string;
  formNotes: string;
  selectApartment: string;
  reservationCreated: string;
  compact: string;
  past: string;
  showPastReservations: string;
  itemsPerPage: string;
  showing: (shown: number, total: number, page: number, totalPages: number) => string;
  tableNum: string;
  tableGuest: string;
  tableApartment: string;
  tableCheckin: string;
  tableGuests: string;
  tableGuestbook: string;
  tableUbyport: string;
  tableGuestForm: string;
  tableManage: string;
  tableEdit: string;
  tableStatus: string;
  tableActions: string;
  submitting: string;
  submitAll: string;
  guestForm: string;
  manage: string;
  ubyportSentSuccess: string;
  statusSent: string;
  statusSending: string;
  statusFailed: string;
  statusUnavailable: string;
  statusPartial: string;
  statusNotSent: string;
  statusCompleted: string;
  statusIncomplete: string;
  statusDeprecated: string;
  cityTaxPaidLabel: string;
  cityTaxUnpaidLabel: string;
  statusNotFilled: string;
  cancelledLabel: string;
  deprecatedLabel: string;
  inactiveConflict: string;
  inactiveLabel: string;
  confirmSubmitUbyport: (guestName: string) => string;
  calendarSyncHint: string;
  calendarSyncLink: string;
  emptyNoApartmentsTitle: string;
  emptyNoApartmentsBody: string;
  emptyNoApartmentsCta: string;
  noReservationsFoundTitle: string;
  includePast: string;
  upcoming: string;
}

export const reservationsText: Record<Lang, ReservationsText> = {
  cs: {
    pageTitle: 'Všechny rezervace',
    addReservation: 'Přidat rezervaci',
    filterTitle: 'Filtrovat rezervace',
    filterApartment: 'Apartmán:',
    filterAllApartments: 'Všechny apartmány',
    filterCheckInAfter: 'Příjezd od:',
    filterCheckOutBefore: 'Odjezd do:',
    filterGuestName: 'Jméno hosta:',
    filterGuestPhone: 'Telefon hosta:',
    searchByName: 'Hledat podle jména…',
    searchInNotes: 'Hledat v poznámkách…',
    loadingReservations: 'Načítám rezervace…',
    noReservations: 'Žádné rezervace nenalezeny. Zkuste upravit filtry.',
    newReservationTitle: 'Nová rezervace',
    formApartment: 'Apartmán',
    formGuestName: 'Jméno hosta',
    formCheckInDate: 'Datum příjezdu',
    formCheckOutDate: 'Datum odjezdu',
    formNumberOfGuests: 'Počet hostů',
    formNotes: 'Poznámky',
    selectApartment: 'Vyberte apartmán',
    reservationCreated: 'Rezervace vytvořena',
    compact: 'Kompaktní',
    past: 'Minulé',
    showPastReservations: 'Zobrazit minulé rezervace',
    itemsPerPage: 'Položek na stránku:',
    showing: (shown, total, page, totalPages) =>
      `Zobrazuji ${shown} z ${total} rezervací${totalPages > 1 ? ` (Stránka ${page} z ${totalPages})` : ''}`,
    tableNum: '#',
    tableGuest: 'Host',
    tableApartment: 'Apartmán',
    tableCheckin: 'Příjezd → Odjezd',
    tableGuests: 'hostů',
    tableGuestbook: 'Guestbook',
    tableUbyport: 'Ubyport',
    tableGuestForm: 'Formulář',
    tableManage: 'Správa',
    tableEdit: 'Upravit',
    tableStatus: 'Stav',
    tableActions: 'Akce',
    submitting: 'Odesílám…',
    submitAll: 'Odeslat vše',
    guestForm: 'Formulář',
    manage: 'Správa',
    ubyportSentSuccess: 'Úspěšně odesláno do Ubyport.',
    statusSent: 'ODESLÁNO',
    statusSending: 'ODESÍLÁM',
    statusFailed: 'SELHALO',
    statusUnavailable: 'NEDOSTUPNÉ',
    statusPartial: 'ČÁSTEČNÉ',
    statusNotSent: 'NEODESLÁNO',
    cityTaxPaidLabel: 'Zaplaceno',
    cityTaxUnpaidLabel: 'Nezaplaceno',
    statusCompleted: 'DOKONČENO',
    statusIncomplete: 'NEDOKONČENO',
    statusDeprecated: 'ARCHIVOVÁNO',
    statusNotFilled: 'NEVYPLNĚNO',
    cancelledLabel: 'Zrušeno',
    deprecatedLabel: 'Archivováno',
    inactiveConflict: 'Neaktivní — konflikt přebukování',
    inactiveLabel: 'Neaktivní',
    confirmSubmitUbyport: (guestName) => `Odeslat všechny kompletní hosty pro „${guestName}" do Ubyport?`,
    calendarSyncHint: 'Tip: Synchronizujte rezervační kalendář (Airbnb, Booking.com…) a rezervace se zobrazí automaticky.',
    calendarSyncLink: 'Propojení kalendáře →',
    emptyNoApartmentsTitle: 'Vítejte v Guestbooku!',
    emptyNoApartmentsBody: 'Zatím nemáte přidaný žádný apartmán. Přidejte první apartmán a začněte přijímat rezervace a spravovat hosty.',
    emptyNoApartmentsCta: 'Přidat první apartmán →',
    noReservationsFoundTitle: 'Žádné rezervace nenalezeny',
    includePast: 'Včetně minulých',
    upcoming: 'Nadcházející',
  },
  en: {
    pageTitle: 'All Reservations',
    addReservation: 'Add Reservation',
    filterTitle: 'Filter Reservations',
    filterApartment: 'Apartment:',
    filterAllApartments: 'All Apartments',
    filterCheckInAfter: 'Check-in After:',
    filterCheckOutBefore: 'Check-out Before:',
    filterGuestName: 'Guest Name:',
    filterGuestPhone: 'Guest Phone:',
    searchByName: 'Search by name...',
    searchInNotes: 'Search in notes...',
    loadingReservations: 'Loading reservations...',
    noReservations: 'No reservations found. Try adjusting your filters.',
    newReservationTitle: 'New Reservation',
    formApartment: 'Apartment',
    formGuestName: 'Guest Name',
    formCheckInDate: 'Check-in Date',
    formCheckOutDate: 'Check-out Date',
    formNumberOfGuests: 'Number of Guests',
    formNotes: 'Notes',
    selectApartment: 'Select apartment',
    reservationCreated: 'Reservation created',
    compact: 'Compact',
    past: 'Past',
    showPastReservations: 'Show past reservations',
    itemsPerPage: 'Items per page:',
    showing: (shown, total, page, totalPages) =>
      `Showing ${shown} of ${total} reservations${totalPages > 1 ? ` (Page ${page} of ${totalPages})` : ''}`,
    tableNum: '#',
    tableGuest: 'Guest',
    tableApartment: 'Apartment',
    tableCheckin: 'Check-in → Check-out',
    tableGuests: 'guests',
    tableGuestbook: 'Guestbook',
    tableUbyport: 'Ubyport',
    tableGuestForm: 'Guest Form',
    tableManage: 'Manage',
    tableEdit: 'Edit',
    tableStatus: 'Status',
    tableActions: 'Actions',
    submitting: 'Submitting…',
    submitAll: 'Submit All',
    guestForm: 'Guest Form',
    manage: 'Manage',
    ubyportSentSuccess: 'Successfully submitted to Ubyport.',
    statusSent: 'SENT',
    statusSending: 'SENDING',
    statusFailed: 'FAILED',
    statusUnavailable: 'UNAVAILABLE',
    statusPartial: 'PARTIAL',
    statusNotSent: 'NOT SENT',
    cityTaxPaidLabel: 'Paid',
    cityTaxUnpaidLabel: 'Unpaid',
    statusCompleted: 'COMPLETED',
    statusIncomplete: 'INCOMPLETE',
    statusDeprecated: 'DEPRECATED',
    statusNotFilled: 'NOT FILLED',
    cancelledLabel: 'Cancelled',
    deprecatedLabel: 'Deprecated',
    inactiveConflict: 'Inactive — overbooking conflict',
    inactiveLabel: 'Inactive',
    confirmSubmitUbyport: (guestName) => `Submit all complete guests for "${guestName}" to Ubyport?`,
    calendarSyncHint: 'Tip: Sync your booking calendar (Airbnb, Booking.com…) and reservations will appear automatically.',
    calendarSyncLink: 'Calendar settings →',
    emptyNoApartmentsTitle: 'Welcome to Guestbook!',
    emptyNoApartmentsBody: 'You have no apartments yet. Add your first apartment to start accepting reservations and managing guests.',
    emptyNoApartmentsCta: 'Add your first apartment →',
    noReservationsFoundTitle: 'No reservations found',
    includePast: 'Include past',
    upcoming: 'Upcoming',
  },
  vi: {
    pageTitle: 'Tất cả lịch đặt',
    addReservation: 'Thêm lịch đặt',
    filterTitle: 'Lọc lịch đặt',
    filterApartment: 'Căn hộ:',
    filterAllApartments: 'Tất cả căn hộ',
    filterCheckInAfter: 'Nhận phòng sau:',
    filterCheckOutBefore: 'Trả phòng trước:',
    filterGuestName: 'Tên khách:',
    filterGuestPhone: 'Điện thoại khách:',
    searchByName: 'Tìm theo tên…',
    searchInNotes: 'Tìm trong ghi chú…',
    loadingReservations: 'Đang tải lịch đặt…',
    noReservations: 'Không tìm thấy lịch đặt. Thử điều chỉnh bộ lọc.',
    newReservationTitle: 'Lịch đặt mới',
    formApartment: 'Căn hộ',
    formGuestName: 'Tên khách',
    formCheckInDate: 'Ngày nhận phòng',
    formCheckOutDate: 'Ngày trả phòng',
    formNumberOfGuests: 'Số lượng khách',
    formNotes: 'Ghi chú',
    selectApartment: 'Chọn căn hộ',
    reservationCreated: 'Đã tạo lịch đặt',
    compact: 'Gọn',
    past: 'Đã qua',
    showPastReservations: 'Hiển thị lịch đặt đã qua',
    itemsPerPage: 'Mỗi trang:',
    showing: (shown, total, page, totalPages) =>
      `Hiển thị ${shown} / ${total} lịch đặt${totalPages > 1 ? ` (Trang ${page} / ${totalPages})` : ''}`,
    tableNum: '#',
    tableGuest: 'Khách',
    tableApartment: 'Căn hộ',
    tableCheckin: 'Nhận → Trả',
    tableGuests: 'khách',
    tableGuestbook: 'Sổ lưu trú',
    tableUbyport: 'Ubyport',
    tableGuestForm: 'Mẫu lưu trú',
    tableManage: 'Quản lý',
    tableEdit: 'Sửa',
    tableStatus: 'Trạng thái',
    tableActions: 'Hành động',
    submitting: 'Đang gửi…',
    submitAll: 'Gửi tất cả',
    guestForm: 'Biểu mẫu',
    manage: 'Quản lý',
    ubyportSentSuccess: 'Đã gửi thành công đến Ubyport.',
    statusSent: 'ĐÃ GỬI',
    statusSending: 'ĐANG GỬI',
    statusFailed: 'THẤT BẠI',
    statusUnavailable: 'KHÔNG KHẢ DỤNG',
    statusPartial: 'MỘT PHẦN',
    statusNotSent: 'CHƯA GỬI',
    cityTaxPaidLabel: 'Đã thanh toán',
    cityTaxUnpaidLabel: 'Chưa thanh toán',
    statusCompleted: 'HOÀN THÀNH',
    statusIncomplete: 'CHƯA XONG',
    statusDeprecated: 'LƯU TRỮ',
    statusNotFilled: 'CHƯA ĐIỀN',
    cancelledLabel: 'Đã hủy',
    deprecatedLabel: 'Đã lưu trữ',
    inactiveConflict: 'Không hoạt động — xung đột lịch đặt',
    inactiveLabel: 'Không hoạt động',
    confirmSubmitUbyport: (guestName) => `Gửi tất cả khách hoàn thành cho "${guestName}" đến Ubyport?`,
    calendarSyncHint: 'Mẹo: Đồng bộ lịch đặt phòng (Airbnb, Booking.com…) và lịch đặt sẽ tự động hiển thị.',
    calendarSyncLink: 'Cài đặt lịch →',
    emptyNoApartmentsTitle: 'Chào mừng đến Guestbook!',
    emptyNoApartmentsBody: 'Bạn chưa có căn hộ nào. Thêm căn hộ đầu tiên để bắt đầu nhận lịch đặt và quản lý khách.',
    emptyNoApartmentsCta: 'Thêm căn hộ đầu tiên →',
    noReservationsFoundTitle: 'Không tìm thấy lịch đặt nào',
    includePast: 'Gồm cả đã qua',
    upcoming: 'Sắp tới',
  },
  uk: {
    pageTitle: 'Всі бронювання',
    addReservation: 'Додати бронювання',
    filterTitle: 'Фільтр бронювань',
    filterApartment: 'Апартамент:',
    filterAllApartments: 'Всі апартаменти',
    filterCheckInAfter: 'Заїзд після:',
    filterCheckOutBefore: 'Виїзд до:',
    filterGuestName: 'Ім\'я гостя:',
    filterGuestPhone: 'Телефон гостя:',
    searchByName: 'Пошук за ім\'ям…',
    searchInNotes: 'Пошук у нотатках…',
    loadingReservations: 'Завантаження бронювань…',
    noReservations: 'Бронювань не знайдено. Спробуйте змінити фільтри.',
    newReservationTitle: 'Нове бронювання',
    formApartment: 'Апартамент',
    formGuestName: 'Ім\'я гостя',
    formCheckInDate: 'Дата заїзду',
    formCheckOutDate: 'Дата виїзду',
    formNumberOfGuests: 'Кількість гостей',
    formNotes: 'Нотатки',
    selectApartment: 'Оберіть апартамент',
    reservationCreated: 'Бронювання створено',
    compact: 'Компактно',
    past: 'Минулі',
    showPastReservations: 'Показати минулі бронювання',
    itemsPerPage: 'На сторінку:',
    showing: (shown, total, page, totalPages) =>
      `Показано ${shown} з ${total} бронювань${totalPages > 1 ? ` (Сторінка ${page} з ${totalPages})` : ''}`,
    tableNum: '#',
    tableGuest: 'Гість',
    tableApartment: 'Апартамент',
    tableCheckin: 'Заїзд → Виїзд',
    tableGuests: 'гостей',
    tableGuestbook: 'Guestbook',
    tableUbyport: 'Ubyport',
    tableGuestForm: 'Форма',
    tableManage: 'Керування',
    tableEdit: 'Редагувати',
    tableStatus: 'Статус',
    tableActions: 'Дії',
    submitting: 'Надсилання…',
    submitAll: 'Надіслати все',
    guestForm: 'Форма',
    manage: 'Керування',
    ubyportSentSuccess: 'Успішно надіслано до Ubyport.',
    statusSent: 'НАДІСЛАНО',
    statusSending: 'НАДСИЛАННЯ',
    statusFailed: 'ПОМИЛКА',
    statusUnavailable: 'НЕДОСТУПНО',
    statusPartial: 'ЧАСТКОВО',
    statusNotSent: 'НЕ НАДІСЛАНО',
    cityTaxPaidLabel: 'Сплачено',
    cityTaxUnpaidLabel: 'Не сплачено',
    statusCompleted: 'ЗАВЕРШЕНО',
    statusIncomplete: 'НЕЗАВЕРШЕНО',
    statusDeprecated: 'АРХІВОВАНО',
    statusNotFilled: 'НЕ ЗАПОВНЕНО',
    cancelledLabel: 'Скасовано',
    deprecatedLabel: 'Архівовано',
    inactiveConflict: 'Неактивне — конфлікт перебронювання',
    inactiveLabel: 'Неактивне',
    confirmSubmitUbyport: (guestName) => `Надіслати всіх завершених гостей для «${guestName}» до Ubyport?`,
    calendarSyncHint: 'Порада: Синхронізуйте календар бронювань (Airbnb, Booking.com…) і бронювання з\'являться автоматично.',
    calendarSyncLink: 'Налаштування календаря →',
    emptyNoApartmentsTitle: 'Ласкаво просимо до Guestbook!',
    emptyNoApartmentsBody: 'У вас ще немає апартаментів. Додайте перший апартамент, щоб розпочати приймати бронювання та керувати гостями.',
    emptyNoApartmentsCta: 'Додати перший апартамент →',
    noReservationsFoundTitle: 'Бронювань не знайдено',
    includePast: 'Включно з минулими',
    upcoming: 'Майбутні',
  },
};
